import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import usePricingPlan, { usePricingPlanForm } from '@hooks/usePricingPlan'

import type { PricingPlanModel } from '@models/types'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditPricingPlanModal'

type ModalCallbacks = {
  closeModal: () => void,
  createPricingPlan: (payload: HandleCallbackActionParams) => Promise<object>,
  showModal: boolean,
  updatePricingPlan: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditPricingPlanModalProps = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<PricingPlanModel>,
  updateFn: Function,
}

const createOrEditPricingPlan = (params: CreateOrEditPricingPlanModalProps) => {
  const {
    callbacks, createFn, entityState, updateFn,
  } = params

  const { closeModal, createPricingPlan, updatePricingPlan } = callbacks || {}

  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const pricingPlanPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Pricing Plan ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updatePricingPlan(pricingPlanPayload)

  // @ts-expect-error TS2345
  return createPricingPlan(pricingPlanPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditPricingPlanModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { pricingPlan } = modalPayload

  const {
    callbacks: {
      createPricingPlan: createFn,
      updatePricingPlan: updateFn,
    },
    creating,
    updating,
  } = usePricingPlan(pricingPlan)

  const {
    entityState,
    entityState: {
      id,
      is_free,
      is_prepaid,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = usePricingPlanForm(pricingPlan, { validateOn: 'change' })

  const updateOrCreateButtonText = id ? 'Save Pricing Plan' : 'Create Pricing Plan'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Pricing Plan" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Prepaid" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="boolean" name="is_prepaid" value={is_prepaid} {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field label="Free" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="boolean" name="is_free" value={is_free} {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrEditPricingPlan({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPricingPlanModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
